import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { notificationSound } from "@/lib/notification-sound";
import { cacheTables, getCachedTables } from "@/lib/pdv-cache";
import { getErrorMessage, logError } from "@/lib/error-messages";
import { sortByNumberField } from "@/lib/utils";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import { useSQLiteLocal, LocalTable } from "./useSQLiteLocal";
import { getIsOnline } from "@/lib/connectivity";
import { getTables } from "@/lib/dataGateway";

export type TableStatus = "available" | "occupied" | "reserved" | "grouped";

export interface RestaurantTable {
  id: string;
  restaurant_id: string;
  number: string;
  name: string | null;
  capacity: number;
  is_active: boolean;
  status: TableStatus;
  current_order_id: string | null;
  grouped_to_order_id: string | null;
  service_tax_enabled: boolean;
  created_at: string;
  updated_at: string;
}

// Helper to map SQLite LocalTable to RestaurantTable
function mapLocalToRestaurantTable(localTables: LocalTable[]): RestaurantTable[] {
  return localTables.map(t => ({
    id: t.id,
    restaurant_id: t.restaurant_id,
    number: t.number,
    name: t.name,
    capacity: 4, // SQLite doesn't store capacity, use default
    is_active: t.is_active === 1,
    status: (t.status as TableStatus) || 'available',
    current_order_id: t.current_order_id,
    grouped_to_order_id: t.grouped_to_order_id || null,
    service_tax_enabled: t.service_tax_enabled === 1,
    created_at: '',
    updated_at: '',
  }));
}

export function useTables() {
  const { activeRestaurant } = useRestaurantContext();
  const queryClient = useQueryClient();
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const previousTablesRef = useRef<RestaurantTable[]>([]);
  const { logOperation } = useOperationLogs();
  const { getTables: getLocalTables, isReady: sqliteReady } = useSQLiteLocal();

  const fetchTables = useCallback(async () => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Local-first fallback: SQLite (has latest offline status) → IndexedDB pdv-cache
    const loadLocalTables = async (): Promise<RestaurantTable[] | null> => {
      if (sqliteReady) {
        const local = getLocalTables();
        if (local.length > 0) {
          return sortByNumberField(mapLocalToRestaurantTable(local));
        }
      }
      const cached = await getCachedTables<RestaurantTable>(activeRestaurant.id);
      return cached ? sortByNumberField(cached) : null;
    };

    try {
      // Offline mode (heartbeat-based) - use local cache
      if (!getIsOnline()) {
        const local = await loadLocalTables();
        if (local) {
          setTables(local);
          previousTablesRef.current = local;
          console.log('[useTables] Loaded from local cache (offline):', local.length);
        } else {
          toast.error("Sem conexão e sem cache de mesas");
        }
        return;
      }

      // Online mode - fetch from network (via DataGateway: local server -> Supabase)
      const { data, error, source } = await getTables<RestaurantTable>(activeRestaurant.id);
      if (import.meta.env.DEV && !error) {
        console.debug(`[useTables] fonte: ${source}`);
      }

      if (error) {
        // Network/Supabase error - try local cache (SQLite → IndexedDB)
        const local = await loadLocalTables();
        if (local) {
          setTables(local);
          previousTablesRef.current = local;
          console.log('[useTables] Restored from local cache (supabase error):', local.length);
        } else {
          toast.error("Erro ao carregar mesas");
        }
      } else {
        const tablesData = sortByNumberField(data as RestaurantTable[]);
        setTables(tablesData);
        previousTablesRef.current = tablesData;
        // Cache for offline use (fire-and-forget to prevent blocking)
        cacheTables(activeRestaurant.id, data || []).catch(() => {});
      }
    } catch (error) {
      // Lie-fi / AbortError — try local fallback before giving up
      try {
        const local = await loadLocalTables();
        if (local) {
          setTables(local);
          previousTablesRef.current = local;
          console.log('[useTables] Restored from local cache (fetch failed):', local.length);
        } else {
          console.error("Error fetching tables:", error);
        }
      } catch (e) {
        console.error("Error fetching tables:", error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant, sqliteReady, getLocalTables]);

  const createTable = async (data: {
    number: string;
    name?: string;
    capacity?: number;
    service_tax_enabled?: boolean;
  }) => {
    if (!activeRestaurant) return null;

    try {
      const { data: newTable, error } = await supabase
        .from("tables")
        .insert({
          restaurant_id: activeRestaurant.id,
          number: data.number,
          name: data.name || null,
          capacity: data.capacity || 4,
          service_tax_enabled: data.service_tax_enabled ?? true,
        })
        .select()
        .single();

      if (error) throw error;

      await logOperation("create", "table", newTable.id, {
        number: newTable.number,
        name: newTable.name,
        capacity: newTable.capacity,
      });

      // Note: Don't add to state here - the realtime subscription will handle it
      toast.success("Mesa criada com sucesso!");
      return newTable;
    } catch (error: any) {
      logError(error, "useTables.createTable");
      toast.error(getErrorMessage(error, "criar", "table"));
      return null;
    }
  };

  const createBatchTables = async (startNumber: number, endNumber: number, capacity: number = 4) => {
    if (!activeRestaurant) return null;

    try {
      const tablesToCreate = [];
      for (let i = startNumber; i <= endNumber; i++) {
        tablesToCreate.push({
          restaurant_id: activeRestaurant.id,
          number: String(i).padStart(2, "0"),
          capacity,
          service_tax_enabled: true,
        });
      }

      const { data, error } = await supabase
        .from("tables")
        .insert(tablesToCreate)
        .select();

      if (error) throw error;

      await logOperation("create", "table", undefined, {
        batch: true,
        startNumber,
        endNumber,
        count: tablesToCreate.length,
      });

      await fetchTables();
      toast.success(`${tablesToCreate.length} mesas criadas com sucesso!`);
      return data;
    } catch (error: any) {
      logError(error, "useTables.createBatchTables");
      toast.error(getErrorMessage(error, "criar", "table"));
      return null;
    }
  };

  const updateTable = async (
    id: string,
    data: Partial<Pick<RestaurantTable, "number" | "name" | "capacity" | "is_active" | "status" | "service_tax_enabled">>
  ) => {
    const currentTable = tables.find(t => t.id === id);
    
    try {
      const { data: updated, error } = await supabase
        .from("tables")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      await logOperation("update", "table", id, {
        number: updated.number,
        changes: data,
      });

      setTables((prev) =>
        prev.map((t) => (t.id === id ? (updated as RestaurantTable) : t))
      );
      toast.success("Mesa atualizada!");
      return updated;
    } catch (error: any) {
      logError(error, "useTables.updateTable");
      toast.error(getErrorMessage(error, "atualizar", "table"));
      return null;
    }
  };

  const deleteTable = async (id: string) => {
    const table = tables.find(t => t.id === id);
    
    try {
      const { error } = await supabase.from("tables").delete().eq("id", id);

      if (error) throw error;

      await logOperation("delete", "table", id, {
        number: table?.number,
        name: table?.name,
      });

      setTables((prev) => prev.filter((t) => t.id !== id));
      toast.success("Mesa removida!");
      return true;
    } catch (error) {
      logError(error, "useTables.deleteTable");
      toast.error(getErrorMessage(error, "excluir", "table"));
      return false;
    }
  };

  const getActiveTables = useCallback(() => {
    return tables.filter((t) => t.is_active);
  }, [tables]);

  const getAvailableTables = useCallback(() => {
    return tables.filter((t) => t.is_active && t.status === "available");
  }, [tables]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Polling for SQLite changes when offline (to detect status updates from offline operations)
  useEffect(() => {
    if (getIsOnline() || !sqliteReady || !activeRestaurant) return;

    const pollInterval = setInterval(() => {
      const localTables = getLocalTables();
      if (localTables.length > 0) {
        const mapped = sortByNumberField(mapLocalToRestaurantTable(localTables));
        
        // Check if any status changed
        const hasChanges = mapped.some(newT => {
          const oldT = previousTablesRef.current.find(t => t.id === newT.id);
          return oldT && oldT.status !== newT.status;
        });

        if (hasChanges) {
          console.log('[useTables] Detected status change in SQLite, updating...');
          setTables(mapped);
          previousTablesRef.current = mapped;
        }
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [sqliteReady, activeRestaurant, getLocalTables]);
  // Listen for offline sync complete event to refetch tables from server
  useEffect(() => {
    const handleSyncComplete = () => {
      console.log('[useTables] Offline sync complete, refreshing tables from server...');
      fetchTables();
    };
    
    window.addEventListener('offline-sync-complete', handleSyncComplete);
    return () => window.removeEventListener('offline-sync-complete', handleSyncComplete);
  }, [fetchTables]);

  useEffect(() => {
    if (!activeRestaurant) return;

    const channel = supabase
      .channel(`tables-${activeRestaurant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tables',
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        async (payload) => {
          // PROTECTION: Ignore remote updates while offline
          // Local SQLite is the source of truth when offline
          if (!getIsOnline()) {
            console.log('[useTables] Ignoring remote update while offline');
            return;
          }
          
          if (payload.eventType === 'INSERT') {
            setTables((prev) => sortByNumberField([...prev, payload.new as RestaurantTable]));
          } else if (payload.eventType === 'UPDATE') {
            const newTable = payload.new as RestaurantTable;
            const oldTable = previousTablesRef.current.find(t => t.id === newTable.id);
            
            // Play sound if status changed
            if (oldTable && oldTable.status !== newTable.status) {
              notificationSound.playTableStatusChange();
            }
            
            setTables((prev) => {
              const updated = sortByNumberField(prev.map((t) =>
                t.id === payload.new.id ? newTable : t
              ));
              previousTablesRef.current = updated;
              return updated;
            });

            // Mesa liberada externamente (ex.: pagamento via APK do garçom)
            // → invalidar caches React Query para que listas de mesas/pedidos
            //   reflitam o estado atual sem exigir entrar/sair da mesa
            if (newTable.status === 'available' && oldTable?.status !== 'available') {
              queryClient.invalidateQueries({ queryKey: ["tables"] });
              queryClient.invalidateQueries({ queryKey: ["orders"] });
            }
          } else if (payload.eventType === 'DELETE') {
            setTables((prev) => prev.filter((t) => t.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [activeRestaurant]);

  return {
    tables,
    isLoading,
    fetchTables,
    createTable,
    createBatchTables,
    updateTable,
    deleteTable,
    getActiveTables,
    getAvailableTables,
  };
}
